<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted } from 'vue';
import { useUsage } from './composables/useUsage';

import SummaryCards from './components/SummaryCards.vue';
import ProviderCard from './components/ProviderCard.vue';
import RefreshButton from './components/RefreshButton.vue';
import LoadingSkeleton from './components/LoadingSkeleton.vue';
import ErrorState from './components/ErrorState.vue';
import EmptyState from './components/EmptyState.vue';

import { formatRelativeTime } from './utils/format';

const usage = useUsage();

// Only render cards for providers that are actually configured on the server.
// Unconfigured providers still appear in `data.providers` (so the summary
// counts stay correct), but we hide them from the grid.
const enabledProviders = computed(
  () => usage.data.value?.providers.filter((p) => p.enabled) ?? [],
);

const hasProviders = computed(
  () => enabledProviders.value.length > 0,
);

const lastUpdatedLabel = computed(() =>
  formatRelativeTime(usage.lastFetchedAt.value),
);

async function handleRefresh(): Promise<void> {
  await usage.refresh();
}

function handleKeydown(event: KeyboardEvent): void {
  // Avoid hijacking form inputs.
  const target = event.target as HTMLElement | null;
  if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
    return;
  }
  if (event.key === 'r' || event.key === 'R') {
    event.preventDefault();
    void handleRefresh();
  }
}

onMounted(() => {
  window.addEventListener('keydown', handleKeydown);
});

onBeforeUnmount(() => {
  window.removeEventListener('keydown', handleKeydown);
});

// Stable key for re-rendering summary / provider cards when new data arrives.
const dataKey = computed(() => usage.lastFetchedAt.value ?? 'pending');
</script>

<template>
  <div class="app-shell">
    <header class="app-header">
      <div class="app-header__brand">
        <div class="app-header__brand-mark" aria-hidden="true"></div>
        <span>tokenout</span>
      </div>
      <div class="app-header__meta" aria-live="polite">
        <span>
          上次更新：
          <strong>{{ lastUpdatedLabel }}</strong>
        </span>
        <span
          v-if="usage.isCached.value"
          class="app-header__cached"
          title="本次数据来自 KV 缓存"
        >缓存</span>
      </div>
      <RefreshButton
        :loading="usage.loading.value"
        label="刷新用量"
        @click="handleRefresh"
      />
    </header>

    <main class="app-main">
      <!-- Loading skeleton (state === 'loading' AND no data yet) -->
      <template v-if="usage.state.value === 'loading' && !usage.data.value">
        <LoadingSkeleton />
      </template>

      <!-- Error state -->
      <template v-else-if="usage.state.value === 'error' && !usage.data.value">
        <ErrorState
          :message="usage.error.value ?? '未知错误'"
          :on-retry="handleRefresh"
        />
      </template>

      <!-- Empty state -->
      <template v-else-if="usage.data.value && !hasProviders">
        <EmptyState />
      </template>

      <!-- Successful render -->
      <template v-else-if="usage.data.value">
        <div :key="`summary-${dataKey}`">
          <SummaryCards :data="usage.data.value" />
        </div>

        <h2 class="section-title">
          平台用量
          <span class="section-title__count">{{ enabledProviders.length }}</span>
        </h2>
        <div class="provider-grid" :key="`providers-${dataKey}`">
          <ProviderCard
            v-for="provider in enabledProviders"
            :key="`${provider.id}-${dataKey}`"
            :provider="provider"
          />
        </div>
      </template>

      <!-- Fallback: transient — show skeleton. -->
      <template v-else>
        <LoadingSkeleton />
      </template>
    </main>

    <footer class="app-footer">
      按 R 键立即刷新 · 数据每 60 秒自动刷新 · 切到后台标签时会暂停
    </footer>
  </div>
</template>
